import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Button,
  Box,
  Badge,
  CircularProgress,
  TablePagination,
  TableSortLabel,
  Typography,
  Tooltip,
  Chip
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import ClearIcon from '@material-ui/icons/Clear';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import { makeStyles } from '@material-ui/core/styles';
import useContactList from '../CustomHooks/useContactList';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import StageChangePopover from './StageChangePopover';

// ---- design tokens (matches the approved mockup) ----
const COLORS = {
  green900: '#123524',
  green700: '#1f5c3c',
  green600: '#2c7a4d',
  greenBg: '#f0f6f2',
  ink900: '#1a1d1f',
  ink600: '#5a6268',
  ink400: '#8a9296',
  line: '#e4e7e6',
  lineSoft: '#eef1f0',
  amber: '#b4720b',
  red: '#b8342c',
};

// same hex-in / rgba-out helper used to keep stage chips tinted but quiet,
// purely presentational — does not affect the color value passed in by getStageColor
const withAlpha = (hex, alpha) => {
  if (!hex || hex[0] !== '#') return `rgba(158,158,158,${alpha})`;
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const useStyles = makeStyles(() => ({
  tableContainer: {
    height: '100%',
    maxHeight: 'calc(100vh - 250px)',
    overflow: 'auto',
    border: `1px solid ${COLORS.line}`,
    borderRadius: 10,
  },
  headerCell: {
    backgroundColor: '#fafafb',
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: COLORS.ink400,
    whiteSpace: 'nowrap',
    userSelect: 'none',
    padding: '11px 14px',
    borderBottom: `1px solid ${COLORS.line}`,
    '&:hover $headerIcons': {
      opacity: 1,
    },
  },
  headerInner: {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
},
 headerIcons: {
  display: 'flex',
  alignItems: 'center',
  marginLeft: 0,
  marginTop: 2,
  opacity: 0.45,              // was 0, faint at rest so the row isn't a blank gap
  transition: 'opacity 0.15s ease-in-out',
},
sortLabel: {
  '& .MuiTableSortLabel-icon': { margin: 0, fontSize: 16 },
},
filterBtn: {
  padding: 2,
  marginLeft: 2,
  color: '#bdbdbd',
  '&:hover': { color: COLORS.green700 },
},
  headerIconsActive: {
    opacity: '1 !important',
  },
  
  menuHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 12px 6px',
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#fafcfb !important',
    },
  },
  bodyCell: {
    padding: '10px 14px',
    fontSize: '0.85rem',
    color: COLORS.ink900,
    borderBottom: `1px solid ${COLORS.lineSoft}`, // Moved border to cell level
  },
  flagCell: {
    padding: 0,
    width: 4,
    borderBottom: `1px solid ${COLORS.lineSoft}`, // Keeps bottom border consistent
  },
  flagBar: {
    display: 'block', // Required so height/minHeight work on <span> inside <td>
    width: 4,
    height: '100%',
    minHeight: 36,
  },
  cellText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mutedText: {
    color: COLORS.ink400,
  },
  stageChip: {
    fontWeight: 700,
    fontSize: '0.72rem',
    height: 26,
    border: '1px solid transparent',
  },
  followUpCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },
  followUpDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  followUpText: {
    fontSize: '0.85rem',
  },
}));

const COLUMNS = [
  { id: 'FullName', label: 'Contact Name', width: 150 },
  { id: 'enquiryType', label: 'Enquiry Type', width: 120 },
  { id: 'crmProjectType', label: 'Contact Type', width: 120 },
  { id: 'fellingLicenseNo', label: 'Felling License', width: 120 },
  { id: 'fullAdd', label: 'Address', width: 200 },
  { id: 'crmAddressCity', label: 'County', width: 110 },
  { id: 'crmContactEmail', label: 'Email', width: 180 },
  { id: 'crmStageName', label: 'Stage', width: 130 },
  { id: 'localFollowUpDate', label: 'Follow Up', width: 120 },
  { id: 'lastContactDate', label: 'Last Contact', width: 110 },
  { id: 'actions', label: '', width: 80 },
];

const STATUS_COLOR = {
  overdue: COLORS.red,
  near: COLORS.amber,
};

export default function ContactsListView({
  rows = [],
  loading = false,
  onRowClick,
  getFollowUpStatus,
  stages,
  getStageColor,
  onStageChange,
  onDeleteContact,
  onOpenNotes
}) {
  const classes = useStyles();

  const {
    paginatedRows,
    totalCount,
    activeColUniqueValues,
    sortConfig,
    handleSortClick,
    columnFilters,
    filterMenu,
    openFilterMenu,
    closeFilterMenu,
    toggleFilterValue,
    selectAllColumn,
    clearColumnFilter,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    stageMenu,
    setStageMenu,
    actionMenu,
    setActionMenu,
  } = useContactList(rows);

  const [stagePopover, setStagePopover] = useState({ anchorEl: null, row: null, targetStage: null });

  // Active filter state for the dropdown menu
  const activeSelectedSet = columnFilters[filterMenu.columnKey] ?? new Set();

  const renderFollowUpCell = row => {
    if (!row.localFollowUpDate) {
      return <span className={classes.mutedText}>-</span>;
    }

    const status = getFollowUpStatus ? getFollowUpStatus(row) : null;
    const color = STATUS_COLOR[status];

    if (!color) {
      return <span className={classes.cellText}>{row.localFollowUpDate}</span>;
    }

    const label = status === 'overdue' ? 'Follow up overdue' : 'Follow up due soon';

    return (
      <Tooltip title={label}>
        <Box className={classes.followUpCell}>
          <span className={classes.followUpDot} style={{ backgroundColor: color }} />
          <span className={classes.followUpText} style={{ color, fontWeight: 600 }}>
            {row.localFollowUpDate}
          </span>
        </Box>
      </Tooltip>
    );
  };

  const renderRowFlag = row => {
    const status = getFollowUpStatus ? getFollowUpStatus(row) : null;
    const color = STATUS_COLOR[status] || 'transparent';
    return <span className={classes.flagBar} style={{ backgroundColor: color }} />;
  };

  return (
    <Paper style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader size="small" style={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell} style={{ width: 4, padding: 0 }} />
              {COLUMNS.map((col) => {
                const isSorted = sortConfig.key === col.id;
                const isFiltered = (columnFilters[col.id]?.size ?? 0) > 0;

                return (
                  <TableCell key={col.id} className={classes.headerCell} style={{ width: col.width }}>
                    <div className={classes.headerInner}>
                      <span>{col.label}</span>

                      <div
                        className={`${classes.headerIcons} ${isSorted || isFiltered ? classes.headerIconsActive : ''
                          }`}
                      >
                        <TableSortLabel
                          active={isSorted}
                          direction={isSorted ? sortConfig.direction : 'asc'}
                          onClick={() => handleSortClick(col.id)}
                        />

                        <IconButton
                          size="small"
                          className={classes.filterBtn}
                          onClick={(e) => openFilterMenu(e, col.id)}
                        >
                          <Badge color="primary" variant="dot" invisible={!isFiltered}>
                            <FilterListIcon
                              style={{
                                fontSize: 16,
                                color: isFiltered ? COLORS.green700 : '#bdbdbd',
                              }}
                            />
                          </Badge>
                        </IconButton>
                      </div>
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1} align="center" style={{ padding: 40 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1} align="center" style={{ padding: 32 }}>
                  <Typography variant="body2" color="textSecondary">
                    No contacts found matching criteria.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, index) => {
                const stageColor = getStageColor ? getStageColor(row.crmStageName, 0) : '#9e9e9e';
                return (
                  <TableRow
                    key={row.crmId || index}
                    hover
                    className={classes.clickableRow}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    <TableCell className={classes.flagCell}>{renderRowFlag(row)}</TableCell>

                    <TableCell className={classes.bodyCell}>
                      <span className={classes.cellText} style={{ fontWeight: 600 }}>
                        {row.FullName || '-'}
                      </span>
                      <span className={`${classes.cellText} ${classes.mutedText}`} style={{ fontSize: '0.75rem', marginTop: 2 }}>
                        {row.crmContactPhone || 'No phone'}
                      </span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <span className={`${classes.cellText} ${!row.enquiryType ? classes.mutedText : ''}`}>
                        {row.enquiryType || '-'}
                      </span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <span className={`${classes.cellText} ${!row.crmProjectType ? classes.mutedText : ''}`}>
                        {row.crmProjectType || '-'}
                      </span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <span className={classes.cellText} style={{ fontWeight: 600 }}>
                        {row.fellingLicenseNo || '-'}
                      </span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <span className={`${classes.cellText} ${!row.fullAdd ? classes.mutedText : ''}`}>
                        {row.fullAdd || '-'}
                      </span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <span className={`${classes.cellText}`}>
                        {row.crmAddressCity || row.crmAddressCounty || '-'}
                      </span>
                    </TableCell>
                    {/* <TableCell className={classes.bodyCell}>
                      <span className={`${classes.cellText} ${!row.crmContactPhone ? classes.mutedText : ''}`}>
                        {row.crmContactPhone || '-'}
                      </span>
                    </TableCell> */}
                    <TableCell className={classes.bodyCell}>
                      <span className={`${classes.cellText} ${!row.crmContactEmail ? classes.mutedText : ''}`}>
                        {row.crmContactEmail || '-'}
                      </span>
                    </TableCell>

                    <TableCell className={classes.bodyCell} onClick={e => e.stopPropagation()}>
                      <Chip
                        label={row.crmStageName || 'Unassigned'}
                        size="small"
                        clickable
                        onClick={e => setStageMenu({ anchorEl: e.currentTarget, row })}
                        deleteIcon={<ArrowDropDownIcon style={{ color: stageColor }} />}
                        onDelete={e => setStageMenu({ anchorEl: e.currentTarget, row })}
                        className={classes.stageChip}
                        style={{
                          backgroundColor: withAlpha(stageColor, 0.12),
                          color: stageColor,
                          borderColor: withAlpha(stageColor, 0.3),
                        }}
                      />
                    </TableCell>

                    <TableCell className={classes.bodyCell}>{renderFollowUpCell(row)}</TableCell>

                    <TableCell className={classes.bodyCell}>
                      <span className={`${classes.cellText} ${!row.lastContactDate ? classes.mutedText : ''}`}>
                        {row.lastContactDate || '-'}
                      </span>
                    </TableCell>

                    <TableCell className={classes.bodyCell} onClick={e => e.stopPropagation()} align="center">
                      <Tooltip title="Notes">
                        <IconButton size="small" onClick={() => onOpenNotes && onOpenNotes(row)}>
                          <ChatBubbleOutlineIcon fontSize="small" style={{ fontSize: 18, color: COLORS.green700 }} />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={e => setActionMenu({ anchorEl: e.currentTarget, row })}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[50, 100, 200]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      {/* Live Checkbox Filter Menu */}
      <Menu
        anchorEl={filterMenu.anchorEl}
        open={Boolean(filterMenu.anchorEl)}
        onClose={closeFilterMenu}
        PaperProps={{ style: { maxHeight: 320, width: 220 } }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {filterMenu.columnKey && (
          <>
            <Box className={classes.menuHeader}>
              <Button
                size="small"
                style={{ fontSize: '0.75rem', textTransform: 'none' }}
                onClick={selectAllColumn}
              >
                Select All
              </Button>
              <Button
                size="small"
                color="secondary"
                startIcon={<ClearIcon style={{ fontSize: 12 }} />}
                style={{ fontSize: '0.75rem', textTransform: 'none' }}
                onClick={() => clearColumnFilter()}
              >
                Clear
              </Button>
            </Box>
            <Divider />

            {activeColUniqueValues.length === 0 ? (
              <MenuItem disabled>
                <ListItemText primary="No values found" />
              </MenuItem>
            ) : (
              activeColUniqueValues.map((value) => (
                <MenuItem
                  key={value}
                  dense
                  onClick={() => toggleFilterValue(value)}
                >
                  <Checkbox
                    size="small"
                    checked={activeSelectedSet.has(value)}
                    style={{ padding: 4, marginRight: 6 }}
                    color="primary"
                  />
                  <ListItemText
                    primary={value}
                    primaryTypographyProps={{ style: { fontSize: '0.82rem' } }}
                  />
                </MenuItem>
              ))
            )}
          </>
        )}
      </Menu>

      {/* <Menu anchorEl={stageMenu.anchorEl} open={Boolean(stageMenu.anchorEl)} onClose={() => setStageMenu({ anchorEl: null, row: null })}>
        {stages.map(stage => (
          <MenuItem
            key={stage.value}
            selected={stage.label === stageMenu.row?.crmStageName}
            onClick={() => {
              const updatedRowData = {
                ...stageMenu.row,
                crmStageId: stage?.StageId
              }
              onStageChange && onStageChange(updatedRowData, stage.label);
              setStageMenu({ anchorEl: null, row: null });
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', marginRight: 8, backgroundColor: getStageColor ? getStageColor(stage.label, 0) : '#bdbdbd' }} />
            {stage.label}
          </MenuItem>
        ))}
      </Menu> */}

      <Menu
        anchorEl={stageMenu.anchorEl}
        open={Boolean(stageMenu.anchorEl)}
        onClose={() => setStageMenu({ anchorEl: null, row: null })}
      >
        {stages.map(stage => (
          <MenuItem
            key={stage.value}
            selected={stage.label === stageMenu.row?.crmStageName}
            onClick={() => {
              // Capture anchor element (the chip) and selected target stage
              const currentAnchor = stageMenu.anchorEl;
              const currentRow = stageMenu.row;
              setStageMenu({ anchorEl: null, row: null });

              // Trigger the inline popover attached to the chip
              setStagePopover({ anchorEl: currentAnchor, row: currentRow, targetStage: stage });
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', marginRight: 8, backgroundColor: getStageColor ? getStageColor(stage.label, 0) : '#bdbdbd' }} />
            {stage.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Popover anchored directly next to the Stage Chip */}
      <StageChangePopover
        open={Boolean(stagePopover.anchorEl)}
        anchorEl={stagePopover.anchorEl}
        contact={stagePopover.row}
        targetStage={stagePopover.targetStage}
        onClose={() => setStagePopover({ anchorEl: null, row: null, targetStage: null })}
        onConfirm={(data) => {
          onStageChange && onStageChange(data);
        }}
      />

      <Menu anchorEl={actionMenu.anchorEl} open={Boolean(actionMenu.anchorEl)} onClose={() => setActionMenu({ anchorEl: null, row: null })}>
        <MenuItem
          onClick={() => {
            onDeleteContact && onDeleteContact(actionMenu.row);
            setActionMenu({ anchorEl: null, row: null });
          }}
        >
          Delete Contact
        </MenuItem>
      </Menu>
    </Paper>
  );
}